import { useTheme, type Theme } from '../lib/theme';

/**
 * The theme switch: one control, remembered. The label names the theme you would move to, which
 * is what a person reaching for a switch wants to know.
 */
export function ThemeToggle({ className = '' }: { className?: string }) {
  const [theme, setTheme] = useTheme();
  return <ThemeControl theme={theme} onChange={setTheme} className={className} />;
}

export function ThemeControl({
  theme,
  onChange,
  className = '',
}: {
  theme: Theme;
  onChange: (theme: Theme) => void;
  className?: string;
}) {
  const dark = theme === 'dark';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={dark}
      aria-label={dark ? 'Switch to the light theme' : 'Switch to the dark theme'}
      onClick={() => onChange(dark ? 'light' : 'dark')}
      className={`group inline-flex h-11 items-center gap-2 rounded-full border border-rule2 px-2 text-[12px] text-ink transition-colors duration-150 ease-ea hover:border-accent ${className}`}
    >
      <span className="relative inline-block h-5 w-9 rounded-full bg-chip transition-colors duration-200 ease-ea group-aria-checked:bg-accent">
        <span
          className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-ink transition-transform duration-200 ease-ea group-aria-checked:translate-x-4 group-aria-checked:bg-onAccent"
          aria-hidden="true"
        />
      </span>
      <span className="pr-1.5 font-medium">{dark ? 'Dark' : 'Light'}</span>
    </button>
  );
}

type SwitchProps = { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean };

/**
 * A plain on/off control for settings screens. The button is the 44px hit area; the 24px track
 * is drawn inside it, and the focus ring follows the track rather than the square box.
 */
export function Switch({ checked, onChange, label, disabled = false }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="group inline-flex h-11 w-11 shrink-0 items-center justify-center focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="relative inline-block h-6 w-11 rounded-full bg-chip transition-colors duration-200 ease-ea group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-offset-2 group-focus-visible:outline-accentText group-aria-checked:bg-accent">
        <span
          className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-ink transition-transform duration-200 ease-ea group-aria-checked:translate-x-5 group-aria-checked:bg-onAccent"
          aria-hidden="true"
        />
      </span>
    </button>
  );
}
